class RecommendationSection extends HTMLElement {
  constructor() {
    super();
    this.processedGalleries = new Set();
    this.swiperInstances = new Map();
    this.activeSlide = null;
    this.lastGallery = null;
  }

  connectedCallback() {
    this.observeChanges();
    this.setupGlobalTouchEvents();
  }

  setupGlobalTouchEvents() {
    // Add a global touch event to reset active slide when touching outside
    document.addEventListener('touchstart', (e) => {
      // Check if the touch is outside of any slide
      const isOutsideSlide = !e.target.closest('.swiper-slide');
      
      if (isOutsideSlide && this.activeSlide) {
        this.resetSlide(this.activeSlide);
        this.activeSlide.classList.remove('active');
        this.activeSlide = null;
      }
    });
  }

  resetSlide(slide) {
    const mainImage = slide.querySelector('.main_image');
    const hoverImage = slide.querySelector('.hover_image');
    if (mainImage && hoverImage) {
      mainImage.style.opacity = '1';
      hoverImage.style.opacity = '0';
    }
  }

  observeChanges() {
    const observer = new MutationObserver(() => {
      const galleries = this.querySelectorAll('.product-swiper-component .image-grid-gallery-new');
      
      // Check if there are any new galleries to process
      let newGalleriesFound = false;
      galleries.forEach(gallery => {
        if (!this.processedGalleries.has(gallery)) {
          newGalleriesFound = true;
          this.processedGalleries.add(gallery);
        }
      });

      // Only proceed if we found new galleries
      if (newGalleriesFound) {
        this.setupMobileImageSwap(galleries);
        this.recommendationSwiper(galleries);
      }
    });

    // Continue observing to catch any new galleries
    observer.observe(this, { childList: true, subtree: true });
  }

  setupMobileImageSwap(galleries) {
    const isMobile = window.matchMedia("(max-width: 640px)").matches;
    if (!isMobile) return;

    galleries.forEach(gallery => {
      const slides = gallery.querySelectorAll('.swiper-slide');
      
      slides.forEach(slide => {
        const mainImage = slide.querySelector('.main_image');
        const hoverImage = slide.querySelector('.hover_image');
        
        // Find the nearest parent <a> tag with href attribute
        const link = slide.closest('a[href]') || slide.querySelector('a[href]');

        if (!mainImage || !hoverImage) return;

        let isTouchMoved = false;
        let touchStartTime = 0;

        slide.addEventListener('touchstart', (e) => {
          // Record touch start time for short tap detection
          touchStartTime = Date.now();
          isTouchMoved = false;
          
          // If switching to a new gallery, reset the previous one
          if (this.lastGallery && this.lastGallery !== gallery) {
            const lastActive = this.lastGallery.querySelector('.swiper-slide.active');
            if (lastActive) {
              this.resetSlide(lastActive);
              lastActive.classList.remove('active');
            }
          }

          // Reset previous active slide if exists
          if (this.activeSlide && this.activeSlide !== slide) {
            this.resetSlide(this.activeSlide);
            this.activeSlide.classList.remove('active');
          }

          // Set current slide as active and show hover image immediately
          this.activeSlide = slide;
          mainImage.style.opacity = '0';
          hoverImage.style.opacity = '1';
          slide.classList.add('active');
          this.lastGallery = gallery;
        });

        slide.addEventListener('touchmove', () => {
          isTouchMoved = true;
        });

        slide.addEventListener('touchend', (e) => {
          // Calculate touch duration
          const touchDuration = Date.now() - touchStartTime;
          
          // If touch not moved and duration is short (likely a tap), follow the link
          if (!isTouchMoved && touchDuration < 300) {
            // Get the nearest link again to ensure we have the most current one
            const linkElement = slide.closest('a[href]') || slide.querySelector('a[href]');
            
            if (linkElement) {
              // Prevent default behavior to avoid any conflicts
              e.preventDefault();
              e.stopPropagation();
              
              // Navigate to the link href
              window.location.href = linkElement.getAttribute('href');
            }
          }
        });
      });
    });
  }

  recommendationSwiper(galleries) {
    galleries.forEach((gallery) => {
      // Skip if this gallery already has a Swiper instance
      if (gallery.classList.contains('swiper-initialized')) return;
      
      // Create a unique identifier for this gallery if it doesn't exist
      if (!gallery.dataset.galleryId) {
        const randomId = Math.random().toString(36).substring(2, 9);
        gallery.dataset.galleryId = randomId;
        
        // Add a unique class to this specific gallery
        gallery.classList.add(`gallery-${randomId}`);
        
        // Also add unique identifiers to navigation buttons for this gallery
        const nextButton = gallery.querySelector('.swiper-button-next');
        const prevButton = gallery.querySelector('.swiper-button-prev');
        
        if (nextButton) {
          nextButton.classList.add(`next-${randomId}`);
        }
        
        if (prevButton) {
          prevButton.classList.add(`prev-${randomId}`);
        }
      }
      
      // Initialize Swiper with gallery-specific navigation selectors
      try {
        const galleryId = gallery.dataset.galleryId;
        const swiper = new Swiper(`.gallery-${galleryId}`, {
          spaceBetween: 5,
          slidesPerView: 1,
          initialSlide: 0,
          navigation: {
            nextEl: `.gallery-${galleryId} .next-${galleryId}`,
            prevEl: `.gallery-${galleryId} .prev-${galleryId}`
          }
        });
        
        // Store the swiper instance for potential future reference
        this.swiperInstances.set(gallery, swiper);
      } catch (error) {
        console.error(`Failed to initialize Swiper for gallery ${gallery.dataset.galleryId}:`, error);
      }
    });
  }
}

customElements.define('recommendation-section', RecommendationSection);